import { GiftOutlined, UserOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, InputNumber, Spin } from 'antd';
import bg_home from 'assets/images/bg_home.jpg';
import Countdown from 'components/Countdown/Countdown';
import HeaderMenu from 'components/Header/HeaderMenu';
import HistoryPredictions from 'components/HistoryPredictions';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import qs from 'query-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createPredictions,
  getBestPlayerPredictions,
  getAllSetting,
  getFP,
  getIP,
  getMatchList,
  getPlayers,
  getAsiaTeams,
  getFinalAsiaTeamPredictions,
  getTeams,
  getTeamsChampions,
  getTime,
  getTopScorerPredictions,
  getMostGoalsInMatchPredictions,
  voteWorldCupBestPlayer,
  voteWorldCupChampion,
  voteWorldCupFinalAsiaTeam,
  voteWorldCupFirstGoalScorer,
  voteWorldCupMostGoalsInMatch,
  voteWorldCupTopScorer
} from 'service/base';
import { twMerge } from 'tailwind-merge';
import { dispatchOpenLoginModal } from 'utils/common/authEvents';
import { CONST_SITE, LocalStorageKey } from 'utils/common/const';
import {
  ChampionPredictionTab,
  PlayerPredictionTab,
  VOTE_GRID_PAGE_SIZE
} from './components/WorldCupPredictionTabs';
import RulePage from './rulePage';

const daysOfWeek = {
  Monday: 'Thứ Hai',
  Tuesday: 'Thứ Ba',
  Wednesday: 'Thứ Tư',
  Thursday: 'Thứ Năm',
  Friday: 'Thứ Sáu',
  Saturday: 'Thứ Bảy',
  Sunday: 'Chủ Nhật'
};

const leagues = [
  {
    id: 1,
    name: 'UEFA Champions League',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/f/f5/UEFA_Champions_League.svg/1024px-UEFA_Champions_League.svg.png'
  },
  {
    id: 2,
    name: 'UEFA Europa League',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Europa_League_2021.svg/795px-Europa_League_2021.svg.png'
  },
  {
    id: 3,
    name: 'English Premier League',
    logo: 'https://loodibee.com/wp-content/uploads/Premier-League-Logo.png'
  },
  {
    id: 4,
    name: 'La Liga',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/LaLiga_logo_2023.svg/1200px-LaLiga_logo_2023.svg.png'
  },
  {
    id: 5,
    name: 'Italian Serie A',
    logo: 'https://1000logos.net/wp-content/uploads/2019/01/Italian-Serie-A-Logo.png'
  },
  {
    id: 6,
    name: 'German Bundesliga',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/df/Bundesliga_logo_%282017%29.svg/1200px-Bundesliga_logo_%282017%29.svg.png'
  },
  {
    id: 7,
    name: 'France Ligue 1',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Logo_Ligue_1_McDonald%27s_2024.svg/250px-Logo_Ligue_1_McDonald%27s_2024.svg.png'
  },
  {
    id: 8,
    name: 'FIFA World Cup',
    logo: 'https://upload.wikimedia.org/wikipedia/vi/thumb/1/17/2026_FIFA_World_Cup_emblem.svg/162px-2026_FIFA_World_Cup_emblem.svg.png'
  }
];

/** Site gửi kèm worldcup (backend C168). */
const WC_SITE = CONST_SITE.C168;

/** `true`: bỏ qua API bình chọn, chỉ để chỉnh UI popup. Luôn `false` khi chạy thật. */
const SKIP_WORLD_CUP_VOTE_API_FOR_UI = false;
/** Chỉ dùng khi `SKIP_WORLD_CUP_VOTE_API_FOR_UI === true`: mở popup lỗi thay vì thành công. */
const SKIP_WORLD_CUP_VOTE_UI_SHOW_FAILURE = false;

const predictionTabs = [
  { key: 'match', label: 'Dự đoán trận đấu', icon: '/assets/images/tab/icon_ddtd.png' },
  { key: 'champion', label: 'Đội vô địch', icon: '/assets/images/tab/icon_dvd.png' },
  { key: 'top-scorer', label: 'Vua phá lưới', icon: '/assets/images/tab/icon_vpl.png' },
  { key: 'best-player', label: 'Cầu thủ xuất sắc nhất', icon: '/assets/images/tab/icon_ctxsn.png' },
  {
    key: 'final-asia-team',
    label: 'Đội Châu Á vào sâu nhất',
    icon: '/assets/images/tab/icon_ctgbdt.png'
  },
  {
    key: 'most-goals-match',
    label: 'Đội ghi nhiều bàn nhất 1 trận',
    icon: '/assets/images/tab/icon_tnbtn.png'
  }
];

function unwrapApiList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.result)) return res.result;
  return [];
}

function mapTeamFromApi(t) {
  const codeStr = String(t?.code ?? '').trim();
  const id =
    t?.id != null && String(t.id).trim() !== ''
      ? String(t.id).trim()
      : codeStr.length > 0
        ? codeStr
        : t?._id != null
          ? String(t._id).trim()
          : String(t?.teamId ?? t?.isoCode ?? t?.countryCode ?? '').trim();
  const name = String(t?.name_vi ?? t?.name ?? t?.teamName ?? t?.title ?? t?.label ?? '').trim();
  let flag = t?.flag ?? t?.logo ?? t?.imageUrl ?? t?.image ?? t?.avatar ?? t?.photo ?? '';
  const flagCode = /^[a-z]{2}$/i.test(codeStr) ? codeStr : /^[a-z]{2}$/i.test(id) ? id : '';
  if (!flag && flagCode) {
    flag = `https://flagcdn.com/w80/${String(flagCode).toLowerCase()}.png`;
  }
  if (!id || !name) return null;
  const countryCode = /^[a-z]{2}$/i.test(codeStr)
    ? codeStr.toLowerCase()
    : /^[a-z]{2}$/i.test(id)
      ? String(id).toLowerCase()
      : undefined;
  return { id, name, flag: flag || undefined, countryCode };
}

function mapPlayerFromApi(p) {
  const id = String(p?.id ?? p?._id ?? p?.playerId ?? '').trim();
  const name = p?.name ?? p?.playerName ?? p?.fullName ?? '';
  const photo = p?.avatar ?? p?.photo ?? p?.image ?? p?.imageUrl ?? '';
  const teamName = p?.teamName ?? p?.team_name ?? '';
  if (!id || !name) return null;
  return {
    id,
    name,
    photo: photo || undefined,
    teamName: teamName || undefined,
    teamId: p?.teamId ?? p?.team_id,
    countryCode: p?.countryCode ?? p?.country_code
  };
}

function mapTopScorerPredictionRow(r, index) {
  const id = String(r?.id ?? r?._id ?? r?.playerId ?? '').trim();
  const name = r?.name ?? r?.playerName ?? '';
  const photo = r?.avatar ?? r?.photo ?? r?.image ?? '';
  const countRaw = r?.count ?? r?.votes ?? r?.voteCount ?? 0;
  const votes =
    typeof countRaw === 'number' ? countRaw : Number(String(countRaw).replace(/\D/g, '')) || 0;
  if (!id || !name) return null;
  return {
    id,
    name,
    votes,
    photo: photo || undefined,
    percent: typeof r?.percent === 'number' ? r.percent : undefined,
    teamName: r?.teamName ?? r?.team_name
  };
}

function mapTeamsChampionsRow(r, index) {
  const code = String(r?.code ?? '').trim();
  const name = r?.name_vi || r?.name || r?.teamName || r?.title || r?.label || '';
  const votesRaw = r?.count ?? r?.votes ?? r?.voteCount ?? r?.score ?? 0;
  const votes =
    typeof votesRaw === 'number' ? votesRaw : Number(String(votesRaw).replace(/\D/g, '')) || 0;
  let flag = r?.flag ?? r?.logo ?? r?.imageUrl ?? r?.image ?? '';
  const idGuess = code || (r?.id ?? r?.teamId ?? r?.countryCode);
  const idStr = String(idGuess ?? '').trim();
  if (!flag && idStr && /^[a-z]{2}$/i.test(idStr)) {
    flag = `https://flagcdn.com/w80/${idStr.toLowerCase()}.png`;
  }
  if (!name) return null;
  const safeId = code || idStr || `champion-row-${index}`;
  return { id: safeId, name, votes, flag: flag || undefined, percent: r?.percent };
}

function isClientLoggedIn() {
  return !!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);
}

/** Tài khoản hiển thị khi đã đăng nhập — ưu tiên userAccount, sau đó user_info */
function getAccountWhenLoggedIn() {
  if (!isClientLoggedIn()) return '';
  const saved = localStorage.getItem('userAccount')?.trim();
  if (saved) return saved;
  try {
    const raw = localStorage.getItem(LocalStorageKey.USER_INFO);
    if (!raw) return '';
    const u = JSON.parse(raw);
    return String(u?.username ?? u?.userName ?? u?.account ?? '').trim();
  } catch {
    return '';
  }
}

function resolveMatchFormAccount(rawData) {
  const manual = rawData?.account != null && String(rawData.account).trim() !== '';
  if (manual) return String(rawData.account).trim();
  return getAccountWhenLoggedIn();
}

const C168Page = () => {
  const [isModalHistory, setIsModalHistory] = useState(false);
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [listDataMatch, setListDataMatch] = useState([]);
  const [timeServer, setTimeServer] = useState(null);
  const [IP, setIP] = useState(null);
  const [FP, setFP] = useState(null);
  const [dataSettings, setDataSettings] = useState([]);
  const [isModalRule, setIsModalRule] = useState(false);
  const [isPredictConfirmOpen, setIsPredictConfirmOpen] = useState(false);

  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'today', 'tomorrow'
  const [activePredictionTab, setActivePredictionTab] = useState('match');
  const [pendingMatchPredictions, setPendingMatchPredictions] = useState([]);
  const [championQuery, setChampionQuery] = useState('');
  const [championCountryFilter, setChampionCountryFilter] = useState('all');
  const [isChampionAutoFilled, setIsChampionAutoFilled] = useState(false);
  const [selectedChampionId, setSelectedChampionId] = useState(null);
  const [isChampionVoting, setIsChampionVoting] = useState(false);
  const [topScorerQuery, setTopScorerQuery] = useState('');
  const [topScorerCountryFilter, setTopScorerCountryFilter] = useState('all');
  const [isTopScorerAutoFilled, setIsTopScorerAutoFilled] = useState(false);
  const [selectedTopScorerId, setSelectedTopScorerId] = useState(null);
  const [isTopScorerVoting, setIsTopScorerVoting] = useState(false);
  const [bestPlayerQuery, setBestPlayerQuery] = useState('');
  const [bestPlayerCountryFilter, setBestPlayerCountryFilter] = useState('all');
  const [isBestPlayerAutoFilled, setIsBestPlayerAutoFilled] = useState(false);
  const [selectedBestPlayerId, setSelectedBestPlayerId] = useState(null);
  const [isBestPlayerVoting, setIsBestPlayerVoting] = useState(false);
  const [firstGoalScorerQuery, setFirstGoalScorerQuery] = useState('');
  const [firstGoalScorerCountryFilter, setFirstGoalScorerCountryFilter] = useState('all');
  const [isFirstGoalScorerAutoFilled, setIsFirstGoalScorerAutoFilled] = useState(false);
  const [selectedFirstGoalScorerId, setSelectedFirstGoalScorerId] = useState(null);
  const [isFirstGoalScorerVoting, setIsFirstGoalScorerVoting] = useState(false);
  const [finalAsiaQuery, setFinalAsiaQuery] = useState('');
  const [finalAsiaCountryFilter, setFinalAsiaCountryFilter] = useState('all');
  const [isFinalAsiaAutoFilled, setIsFinalAsiaAutoFilled] = useState(false);
  const [selectedFinalAsiaTeamId, setSelectedFinalAsiaTeamId] = useState(null);
  const [isFinalAsiaVoting, setIsFinalAsiaVoting] = useState(false);
  const [mostGoalsMatchQuery, setMostGoalsMatchQuery] = useState('');
  const [mostGoalsMatchCountryFilter, setMostGoalsMatchCountryFilter] = useState('all');
  const [isMostGoalsMatchAutoFilled, setIsMostGoalsMatchAutoFilled] = useState(false);
  const [selectedMostGoalsMatchId, setSelectedMostGoalsMatchId] = useState(null);
  const [isMostGoalsMatchVoting, setIsMostGoalsMatchVoting] = useState(false);
  const [voteConfirm, setVoteConfirm] = useState({
    open: false,
    type: '',
    title: '',
    selectedName: '',
    selectedImage: '',
    payload: null,
    error: ''
  });
  const [voteSuccessOpen, setVoteSuccessOpen] = useState(false);
  const [voteFailureOpen, setVoteFailureOpen] = useState(false);
  const [voteFailureMessage, setVoteFailureMessage] = useState('');

  const [championTeams, setChampionTeams] = useState([]);
  const [wcPlayers, setWcPlayers] = useState([]);
  const [championLeaderboard, setChampionLeaderboard] = useState([]);
  const [topScorerLeaderboard, setTopScorerLeaderboard] = useState([]);
  const [bestPlayerLeaderboard, setBestPlayerLeaderboard] = useState([]);
  const [firstGoalScorerLeaderboard, setFirstGoalScorerLeaderboard] = useState([]);
  const [finalAsiaTeams, setFinalAsiaTeams] = useState([]);
  const [finalAsiaLeaderboard, setFinalAsiaLeaderboard] = useState([]);
  const [mostGoalsMatchLeaderboard, setMostGoalsMatchLeaderboard] = useState([]);

  // Hàm lọc trận đấu theo tab được chọn
  const getFilteredMatches = useCallback(() => {
    if (!listDataMatch || listDataMatch.length === 0) return [];

    const now = dayjs();
    const today = now.format('YYYY-MM-DD');
    const tomorrow = now.add(1, 'day').format('YYYY-MM-DD');

    switch (activeTab) {
      case 'today':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === today;
        });
      case 'tomorrow':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === tomorrow;
        });
      case 'all':
      default:
        return listDataMatch;
    }
  }, [listDataMatch, activeTab]);

  const [matchListPage, setMatchListPage] = useState(1);

  const predictableMatches = useMemo(() => {
    const matchList = getFilteredMatches();
    if (!matchList || matchList.length <= 0) return [];
    const filteredMatchList =
      selectedLeagues.length > 0
        ? matchList.filter((item) =>
            selectedLeagues.includes(
              leagues.find(
                (league) =>
                  league.name === item.leagueName || item.leagueName.startsWith(league.name)
              )?.id
            )
          )
        : matchList;
    return filteredMatchList.filter((item) => timeServer <= item.countdown);
  }, [getFilteredMatches, selectedLeagues, timeServer]);

  const matchLastPage = Math.max(1, Math.ceil(predictableMatches.length / VOTE_GRID_PAGE_SIZE));

  const paginatedMatches = useMemo(() => {
    const start = (matchListPage - 1) * VOTE_GRID_PAGE_SIZE;
    return predictableMatches.slice(start, start + VOTE_GRID_PAGE_SIZE);
  }, [predictableMatches, matchListPage]);

  useEffect(() => {
    setMatchListPage(1);
  }, [getFilteredMatches, selectedLeagues]);

  useEffect(() => {
    setMatchListPage((p) => (p > matchLastPage ? matchLastPage : p));
  }, [matchLastPage]);

  const handleLeagueClick = (leagueId) => {
    setSelectedLeagues((prevSelectedLeagues) =>
      prevSelectedLeagues.includes(leagueId)
        ? prevSelectedLeagues.filter((id) => id !== leagueId)
        : [...prevSelectedLeagues, leagueId]
    );
  };

  const [scrollPosition, setScrollPosition] = useState(0);
  const leagueListRef = React.createRef();

  const scrollLeft = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: -100, behavior: 'smooth' });
      setScrollPosition(leagueListRef.current.scrollLeft - 100);
    }
  };

  const scrollRight = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: 100, behavior: 'smooth' });
      setScrollPosition(leagueListRef.current.scrollLeft + 100);
    }
  };

  useEffect(() => {
    fetchAllSetting();
    fetchIP();
    fetchFP();
    fetchTimeServer();
  }, []);

  useEffect(() => {
    fetchDataScheduByLS();
    // Load saved account from localStorage
    const savedAccount = localStorage.getItem('saved_account');
    if (savedAccount) {
      form.setFieldsValue({ account: savedAccount });
    }
  }, [form]);

  const topScorerPlayersList = useMemo(() => {
    if (wcPlayers.length > 0) {
      return wcPlayers.map((p) => ({
        id: String(p.id),
        name: p.name,
        teamName: p.teamName,
        countryCode: p.countryCode,
        photo:
          p.photo ||
          `https://ui-avatars.com/api/?size=128&name=${encodeURIComponent(p.name)}&background=f8fafc&color=334155&bold=true`
      }));
    }
    return [];
  }, [wcPlayers]);

  useEffect(() => {
    let cancelled = false;
    const apiParams = { params: { site: WC_SITE } };
    (async () => {
      try {
        const [
          teamsRes,
          playersRes,
          championsRes,
          topScorerPredRes,
          bestPlayerPredRes,
          mostGoalsMatchRes,
          asiaTeamsRes,
          finalAsiaPredRes
        ] = await Promise.all([
          getTeams(apiParams),
          getPlayers(apiParams),
          getTeamsChampions(),
          getTopScorerPredictions(apiParams),
          getBestPlayerPredictions(apiParams),
          getMostGoalsInMatchPredictions(apiParams),
          getAsiaTeams(apiParams),
          getFinalAsiaTeamPredictions(apiParams)
        ]);
        if (cancelled) return;
        const teams = unwrapApiList(teamsRes).map(mapTeamFromApi).filter(Boolean);
        if (teams.length) setChampionTeams(teams);

        const rawPlayers =
          playersRes?.code === 0 && Array.isArray(playersRes?.data)
            ? playersRes.data
            : unwrapApiList(playersRes);
        const players = rawPlayers.map(mapPlayerFromApi).filter(Boolean);
        if (players.length) setWcPlayers(players);

        const rawChampions =
          championsRes?.code === 0 && Array.isArray(championsRes?.data)
            ? championsRes.data
            : unwrapApiList(championsRes);
        const championRows = rawChampions
          .map((row, i) => mapTeamsChampionsRow(row, i))
          .filter(Boolean)
          .sort((a, b) => (b.votes || 0) - (a.votes || 0));
        if (championRows.length) setChampionLeaderboard(championRows);

        const rawTopScorer =
          topScorerPredRes?.code === 0 && Array.isArray(topScorerPredRes?.data)
            ? topScorerPredRes.data
            : unwrapApiList(topScorerPredRes);
        const topScorerRows = rawTopScorer
          .map((row, i) => {
            const m = mapTopScorerPredictionRow(row, i);
            return m ? { m, i } : null;
          })
          .filter(Boolean)
          .sort((a, b) => {
            const dv = (b.m.votes || 0) - (a.m.votes || 0);
            if (dv !== 0) return dv;
            return a.i - b.i;
          })
          .slice(0, 10)
          .map((x) => x.m);
        if (topScorerRows.length) setTopScorerLeaderboard(topScorerRows);

        const rawBestPlayer =
          bestPlayerPredRes?.code === 0 && Array.isArray(bestPlayerPredRes?.data)
            ? bestPlayerPredRes.data
            : unwrapApiList(bestPlayerPredRes);
        const bestPlayerRows = rawBestPlayer
          .map((row, i) => {
            const m = mapTopScorerPredictionRow(row, i);
            return m ? { m, i } : null;
          })
          .filter(Boolean)
          .sort((a, b) => {
            const dv = (b.m.votes || 0) - (a.m.votes || 0);
            if (dv !== 0) return dv;
            return a.i - b.i;
          })
          .slice(0, 10)
          .map((x) => x.m);
        if (bestPlayerRows.length) setBestPlayerLeaderboard(bestPlayerRows);

        const rawAsiaTeams = unwrapApiList(asiaTeamsRes);
        const asiaTeamsMapped = rawAsiaTeams.map(mapTeamFromApi).filter(Boolean);
        if (asiaTeamsMapped.length) setFinalAsiaTeams(asiaTeamsMapped);

        const rawFinalAsia =
          finalAsiaPredRes?.code === 0 && Array.isArray(finalAsiaPredRes?.data)
            ? finalAsiaPredRes.data
            : unwrapApiList(finalAsiaPredRes);
        const finalAsiaRows = rawFinalAsia
          .map((row, i) => mapTeamsChampionsRow(row, i))
          .filter(Boolean)
          .sort((a, b) => (b.votes || 0) - (a.votes || 0));
        if (finalAsiaRows.length) setFinalAsiaLeaderboard(finalAsiaRows);

        const rawMostGoals =
          mostGoalsMatchRes?.code === 0 && Array.isArray(mostGoalsMatchRes?.data)
            ? mostGoalsMatchRes.data
            : unwrapApiList(mostGoalsMatchRes);
        const mostGoalsRows = rawMostGoals
          .map((row, i) => mapTeamsChampionsRow(row, i))
          .filter(Boolean)
          .sort((a, b) => (b.votes || 0) - (a.votes || 0));
        if (mostGoalsRows.length) setMostGoalsMatchLeaderboard(mostGoalsRows);
      } catch (e) {
        console.error('World Cup API:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchIP = async () => {
    try {
      const res = await getIP();
      setIP(res);
    } catch (e) {}
  };

  const fetchFP = async () => {
    try {
      const res = await getFP();
      setFP(res);
    } catch (e) {}
  };

  const fetchTimeServer = async () => {
    try {
      const res = await getTime();
      setTimeServer(res);
    } catch (e) {}
  };

  const fetchDataScheduByLS = async () => {
    try {
      setIsLoading(true);
      const res2 = await getMatchList({
        site: CONST_SITE.C168
      });

      const dataMatch2 =
        res2?.data?.length > 0
          ? res2.data.map((item) => {
              const formatCountDown = dayjs.unix(item.startTime);
              return { ...item, countdown: new Date(formatCountDown) };
            })
          : [];

      setListDataMatch(dataMatch2 || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching match list:', error);
      setIsLoading(false);
    }
  };

  const fetchAllSetting = async (data) => {
    try {
      setIsLoading(true);
      const res = await getAllSetting(qs.stringify({ site: CONST_SITE.C168 }));
      setDataSettings(res?.data || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const TIMEOUT_DURATION = 10000; // 10 seconds

  const createPredictionsWithTimeout = (predictions) => {
    return Promise.race([
      createPredictions(predictions),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), TIMEOUT_DURATION)
      )
    ]);
  };

  const onConfirmMatchPrediction = async () => {
    if (pendingMatchPredictions.length === 0) {
      setIsPredictConfirmOpen(false);
      return;
    }

    try {
      setIsLoadingSubmit(true);

      const res = await createPredictionsWithTimeout(pendingMatchPredictions);
      if (res?.code === 0) {
        // Save account to localStorage on success
        const account = pendingMatchPredictions[0]?.account;
        if (account) {
          localStorage.setItem('prediction_account', account);
        }

        notification.success({
          message: 'Thành công',
          description: 'Dự đoán tỉ số thành công!'
        });
        form.resetFields(Object.keys(form.getFieldsValue()).filter((key) => key !== 'account'));
        setIsPredictConfirmOpen(false);
        setPendingMatchPredictions([]);
        // Reload match list
        fetchDataScheduByLS();
      } else {
        if (res.valid === false) {
          notification.error({
            message: res.title_mess,
            description: res?.text_mess
          });
        } else {
          notification.error({
            message: 'Lỗi',
            description: res?.message
          });
        }
      }
      setIsLoadingSubmit(false);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
      setIsLoadingSubmit(false);
    }
  };

  const submitMatchPredictions = async (rawData) => {
    try {
      if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
        dispatchOpenLoginModal();
        return;
      }

      const account = resolveMatchFormAccount(rawData);
      const data = { ...rawData, account };

      const filteredMatches = getFilteredMatches();
      if (filteredMatches?.length === 0) return;

      if (!data?.account) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập tên đăng nhập hoặc đăng nhập để dùng tài khoản của bạn!'
        });
        return;
      }

      // Validate that both home and away scores are provided for each match
      // Kiểm tra từng trận: nếu đã điền homeScore hoặc awayScore thì phải điền đủ cả hai và sameScore
      const matchIds = Object.keys(data)
        .filter((key) => key.startsWith('homeScore_') || key.startsWith('awayScore_'))
        .map((key) => key.split('_')[1]);

      const uniqueMatchIds = Array.from(new Set(matchIds));

      const hasInvalidScores = uniqueMatchIds.some((matchId) => {
        const homeScore = data[`homeScore_${matchId}`];
        const awayScore = data[`awayScore_${matchId}`];
        const sameScore = data[`sameScore_${matchId}`];

        const hasHome = homeScore !== undefined && homeScore !== null && homeScore !== '';
        const hasAway = awayScore !== undefined && awayScore !== null && awayScore !== '';
        const hasSame = sameScore !== undefined && sameScore !== null && sameScore !== '';

        // Nếu đã điền 1 trong 2 tỉ số thì phải điền đủ cả hai và sameScore
        if ((hasHome || hasAway) && (!hasHome || !hasAway || !hasSame)) {
          return true;
        }
        return false;
      });

      if (hasInvalidScores) {
        notification.warning({
          message: 'Cảnh báo',
          description:
            'Vui lòng nhập đầy đủ cả hai bên tỉ số và số người dự đoán giống bạn cho mỗi trận đấu!'
        });
        return;
      }

      console.log('filteredMatches', filteredMatches);
      const resultGroup = groupScoresByMatchId(data, IP, FP, filteredMatches);

      if (resultGroup.length === 0) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng điền đẩy đủ thông tin và dự đoán ít nhất một trận đấu!'
        });
        return;
      }

      // Open confirm popup before submitting
      setPendingMatchPredictions(resultGroup);
      setIsPredictConfirmOpen(true);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
      setIsLoadingSubmit(false);
    }
  };

  const onFinishForm = async (data) => {
    await submitMatchPredictions(data);
  };

  /** Nút Xác nhận trên từng thẻ trận: cùng luồng với form (toàn bộ trận đã điền), account lấy từ token nếu đã đăng nhập */
  const handleMatchCardConfirmClick = () => {
    submitMatchPredictions(form.getFieldsValue(true));
  };

  const onFinishFailed = (errorInfo) => {
    if (!errorInfo?.values?.account) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đang nhập!'
      });
    } else {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng điền đầy đủ thông tin!'
      });
    }
  };

  const onFinishCountDown = () => {
    console.log('finished!');
  };

  const onCancelMatchConfirm = () => {
    setIsPredictConfirmOpen(false);
  };

  const groupScoresByMatchId = (input, ip, fp, matchInfo) => {
    // Gom lại từng trận, nhận cả trường sameScore
    const groupedMatches = {};
    Object.keys(input).forEach((key) => {
      if (key === 'account') return;
      const parts = key.split('_');
      if (parts.length !== 2) return;
      const [scoreType, matchId] = parts;
      if (!matchId || !['homeScore', 'awayScore', 'sameScore'].includes(scoreType)) return;
      if (!groupedMatches[matchId]) {
        groupedMatches[matchId] = {
          matchId,
          account: input.account,
          homeScore: undefined,
          awayScore: undefined,
          sameScore: undefined
        };
      }
      if (scoreType === 'homeScore') groupedMatches[matchId].homeScore = input[key];
      if (scoreType === 'awayScore') groupedMatches[matchId].awayScore = input[key];
      if (scoreType === 'sameScore') groupedMatches[matchId].sameScore = input[key];
    });

    return Object.values(groupedMatches)
      .filter(
        (match) =>
          match.homeScore !== undefined &&
          match.awayScore !== undefined &&
          match.sameScore !== undefined &&
          match.homeScore !== null &&
          match.awayScore !== null &&
          match.sameScore !== null &&
          match.homeScore !== '' &&
          match.awayScore !== '' &&
          match.sameScore !== ''
      )
      .map(({ matchId, account, homeScore, awayScore, sameScore }) => {
        const matchDetails = matchInfo.find(
          (match) => parseInt(match.matchId) === parseInt(matchId)
        );
        return {
          matchId,
          homeScore,
          awayScore,
          sameScore,
          account,
          ip,
          fp,
          homeName: matchDetails ? matchDetails.homeName : 'Unknown',
          awayName: matchDetails ? matchDetails.awayName : 'Unknown',
          site: SITE
        };
      });
  };

  const renderMatchCard = (item) => {
    const matchDayOfWeek = daysOfWeek[dayjs(item.startTime * 1000).format('dddd')];
    const rowKey = String(item.matchId ?? `${item.startTime}-${item.homeName}-${item.awayName}`);
    return (
      <div
        className={twMerge(
          'flex items-center xl:w-3/4 md:w-4/5 w-full mx-auto bg-white rounded-2xl px-2 text-sm font-medium gap-2 border flex-col shadow-[0_0_10.574px_0_rgba(0,0,0,0.09)_inset] pb-3 mt-2',
          timeServer > item.countdown ? 'contrast-50' : ''
        )}
        key={rowKey}
        style={{
          backgroundImage: `url(assets/images/match/bg.png)`,
          backgroundSize: 'cover'
        }}
      >
        <div
          className={twMerge(
            'italic text-center w-auto ',
            'px-5 md:px-10',
            'rounded-b-[10.448px]',
            'bg-[#26A9E0]',
            'shadow-[0_2.09px_10.448px_0_rgba(0,0,0,0.25)]'
          )}
        >
          <p className="text-white text-center font-roboto font-bold leading-normal pt-2 pb-2 not-italic text-xs sm:text-sm md:text-base lg:text-lg">
            {item?.leagueName}
          </p>
        </div>
        <div className="flex flex-col md:flex-row justify-center md:justify-around gap-0 md:gap-6 w-full items-center text-xs sm:text-sm md:text-base pb-3">
          <div className="flex flex-row items-center ">
            <span className="flex flex-row items-center h-full">
              <span
                className={`mr-1 flex items-center h-full md:text-[16px] text-[14px] ${
                  timeServer > item.countdown ? 'text-red-500' : 'text-black'
                }`}
              >
                {timeServer > item.countdown ? 'Hết giờ' : 'Thời gian dự đoán còn: '}
              </span>
              <div className="countdown-responsive">
                <Countdown
                  value={item.countdown}
                  onFinish={onFinishCountDown}
                  format="HH:mm:ss"
                  style={{ color: '#26a9e0', fontWeight: 600 }}
                />
                <style>
                  {`
                          .countdown-responsive .ant-statistic-content {
                            font-size: 14px;
                          }
                          @media (min-width: 768px) {
                            .countdown-responsive .ant-statistic-content {
                              font-size: 16px;
                            }
                          }
                        `}
                </style>
              </div>
            </span>
          </div>
          <span className="flex items-center text-black">
            <span className="flex items-center h-full md:text-[16px] text-[14px]">
              Thời gian kết thúc:{' '}
            </span>
            <span className="text-[#26A9E0] ml-1 font-semibold flex items-center h-full  md:text-[16px] text-[14px]">
              {dayjs(item.startTime * 1000).format('HH:mm')}
            </span>
          </span>
        </div>
        <div className="flex flex-row items-end justify-center gap-[22px] w-full py-4">
          {/* Home Team */}
          <div className="flex flex-col gap-[11px] items-center w-full max-w-[160px] ">
            <div className="bg-white flex items-center justify-center p-[10px] rounded-full w-[100px] h-[100px]">
              <img
                src={item.homeLogo}
                className="w-[68px] h-[68px] object-contain"
                alt={item.homeName}
              />
            </div>
            <p className="capitalize font-medium text-[13px] text-black text-center w-full">
              {item.homeName}
            </p>
          </div>

          {/* VS and Time */}
          <div className="relative w-[163px] h-[91px]">
            <div className="absolute bottom-0 left-0 w-full h-[45px] flex items-center justify-center px-[6px] py-[5px] rounded-t-[4px]">
              <div className="font-semibold text-[#26a9e0] text-center">
                <p className="font-bold text-[23px] mb-0 leading-none">
                  {dayjs(item.startTime * 1000).format('HH:mm')}
                </p>
                <p className="text-[11px] leading-none mt-1">
                  {`${matchDayOfWeek}, ${dayjs(item.startTime * 1000).format('DD/MM/YYYY')}`}
                </p>
              </div>
            </div>
            <img
              src="assets/images/match/vs.png"
              alt="VS"
              className="absolute top-0 left-1/2 -translate-x-1/2 w-auto h-[38px]"
            />
          </div>

          {/* Away Team */}
          <div className="flex flex-col gap-[13px] items-center w-full max-w-[160px] ">
            <div className="flex items-center justify-center p-[10px] rounded-full w-[100px] h-[100px] bg-white">
              <img
                src={item.awayLogo}
                className="w-[68px] h-[68px] object-contain"
                alt={item.awayName}
              />
            </div>
            <p className="capitalize font-medium text-[13px] text-black text-center w-full">
              {item.awayName}
            </p>
          </div>
        </div>
        <div className="flex flex-col w-full sm:w-3/4 justify-center items-center px-2">
          <div className="flex md:justify-around justify-center flex-row items-center p-1 gap-3 sm:gap-5 md:gap-16">
            <Form.Item className="m-0 p-0" name={`homeScore_${item.matchId}`}>
              <InputNumber
                disabled={timeServer > item.countdown ? true : false}
                controls={false}
                min={0}
                max={99}
                maxLength={2}
                placeholder="Nhập số"
                className="bg-white border-0 w-[70px] sm:w-[90px] md:w-[145px] h-[34px] sm:h-[37px] md:h-[40px] text-center text-xs sm:text-sm md:text-base font-normal text-[#505050] shadow-[0px_1.057px_4.23px_0px_inset_rgba(0,0,0,0.29)] rounded-md"
                style={{
                  textAlign: 'center',
                  fontFamily: "'Roboto', sans-serif"
                }}
              />
            </Form.Item>
            <span className="font-bold text-black text-sm sm:text-base md:text-lg whitespace-nowrap px-1">
              Tỉ số
            </span>
            <Form.Item className="m-0 p-0" name={`awayScore_${item.matchId}`}>
              <InputNumber
                disabled={timeServer > item.countdown ? true : false}
                controls={false}
                min={0}
                max={99}
                maxLength={2}
                placeholder="Nhập số"
                className="bg-white border-0 w-[70px] sm:w-[90px] md:w-[145px] h-[34px] sm:h-[37px] md:h-[40px] text-center text-xs sm:text-sm md:text-base font-normal text-[#505050] shadow-[0px_1.057px_4.23px_0px_inset_rgba(0,0,0,0.29)] rounded-md"
                style={{
                  textAlign: 'center',
                  fontFamily: "'Roboto', sans-serif"
                }}
              />
            </Form.Item>
          </div>
          <div className="flex justify-between flex-row items-center p-1 gap-16">
            <Form.Item className="m-0 p-0" name={`sameScore_${item.matchId}`}>
              <InputNumber
                disabled={timeServer > item.countdown ? true : false}
                controls={false}
                min={0}
                maxLength={5}
                placeholder="Số người dự đoán giống bạn"
                className="bg-white border-0 !w-[290px] h-[37px] text-center text-[14.804px] font-normal text-[#505050] shadow-[0px_1.057px_4.23px_0px_inset_rgba(0,0,0,0.29)]"
                style={{
                  textAlign: 'center',
                  fontFamily: "'Roboto', sans-serif"
                }}
              />
            </Form.Item>
          </div>
        </div>
        <div className="flex flex-row w-full sm:w-3/4 justify-center mt-2">
          <Button
            type="primary"
            htmlType="button"
            onClick={handleMatchCardConfirmClick}
            className="rounded-[40px] border-[0.8px] border-[#F4F4F4] bg-[#26A9E0] shadow-[0px_0px_4px_0px_#63c8f2,0px_0px_20px_0px_#63c8f2_inset] flex w-[140px] sm:w-[163px] h-[38px] sm:h-[40px] md:h-[44px] px-4 sm:px-5 py-2.5 sm:py-3 justify-center items-center text-white font-semibold text-sm sm:text-base"
          >
            <span className="flex items-center justify-center w-full">Xác Nhận</span>
          </Button>
        </div>
      </div>
    );
  };

  const championCountryOptions = useMemo(() => {
    const codes = new Set();
    championTeams.forEach((t) => {
      const c =
        t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : null);
      if (c) codes.add(c);
    });
    const opts = [{ value: 'all', label: 'Tất cả' }];
    const dn = new Intl.DisplayNames(['vi'], { type: 'region' });
    Array.from(codes)
      .sort()
      .forEach((c) => {
        let label = c.toUpperCase();
        try {
          label = dn.of(c.toUpperCase()) || label;
        } catch (_) {}
        opts.push({ value: c, label });
      });
    return opts;
  }, [championTeams]);

  const playerCountryOptions = useMemo(() => {
    const codes = new Set();
    topScorerPlayersList.forEach((p) => {
      if (p.countryCode) codes.add(String(p.countryCode).toLowerCase());
    });
    const opts = [{ value: 'all', label: 'Tất cả' }];
    const dn = new Intl.DisplayNames(['vi'], { type: 'region' });
    Array.from(codes)
      .sort()
      .forEach((c) => {
        let label = c.toUpperCase();
        try {
          label = dn.of(c.toUpperCase()) || label;
        } catch (_) {}
        opts.push({ value: c, label });
      });
    return opts;
  }, [topScorerPlayersList]);

  const filteredChampionTeams = useMemo(() => {
    let list = isChampionAutoFilled
      ? championTeams
      : championTeams.filter((t) =>
          t.name.toLowerCase().includes(championQuery.trim().toLowerCase())
        );
    if (championCountryFilter !== 'all') {
      list = list.filter((t) => {
        const code =
          t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : '');
        return code === championCountryFilter;
      });
    }
    return list;
  }, [isChampionAutoFilled, championTeams, championQuery, championCountryFilter]);

  const selectedChampion =
    championTeams.find((t) => String(t.id) === String(selectedChampionId)) || null;

  const filteredTopScorerPlayers = useMemo(() => {
    let list = isTopScorerAutoFilled
      ? topScorerPlayersList
      : topScorerPlayersList.filter((p) =>
          p.name.toLowerCase().includes(topScorerQuery.trim().toLowerCase())
        );
    if (topScorerCountryFilter !== 'all') {
      list = list.filter(
        (p) => String(p.countryCode || '').toLowerCase() === topScorerCountryFilter
      );
    }
    return list;
  }, [isTopScorerAutoFilled, topScorerPlayersList, topScorerQuery, topScorerCountryFilter]);

  const selectedTopScorer =
    topScorerPlayersList.find((p) => String(p.id) === String(selectedTopScorerId)) || null;

  const filteredBestPlayerPlayers = useMemo(() => {
    let list = isBestPlayerAutoFilled
      ? topScorerPlayersList
      : topScorerPlayersList.filter((p) =>
          p.name.toLowerCase().includes(bestPlayerQuery.trim().toLowerCase())
        );
    if (bestPlayerCountryFilter !== 'all') {
      list = list.filter(
        (p) => String(p.countryCode || '').toLowerCase() === bestPlayerCountryFilter
      );
    }
    return list;
  }, [isBestPlayerAutoFilled, topScorerPlayersList, bestPlayerQuery, bestPlayerCountryFilter]);

  const selectedBestPlayer =
    topScorerPlayersList.find((p) => String(p.id) === String(selectedBestPlayerId)) || null;

  const filteredFirstGoalScorerPlayers = useMemo(() => {
    let list = isFirstGoalScorerAutoFilled
      ? topScorerPlayersList
      : topScorerPlayersList.filter((p) =>
          p.name.toLowerCase().includes(firstGoalScorerQuery.trim().toLowerCase())
        );
    if (firstGoalScorerCountryFilter !== 'all') {
      list = list.filter(
        (p) => String(p.countryCode || '').toLowerCase() === firstGoalScorerCountryFilter
      );
    }
    return list;
  }, [
    isFirstGoalScorerAutoFilled,
    topScorerPlayersList,
    firstGoalScorerQuery,
    firstGoalScorerCountryFilter
  ]);

  const selectedFirstGoalScorer =
    topScorerPlayersList.find((p) => String(p.id) === String(selectedFirstGoalScorerId)) || null;

  const filteredMostGoalsTeams = useMemo(() => {
    let list = isMostGoalsMatchAutoFilled
      ? championTeams
      : championTeams.filter((t) =>
          t.name.toLowerCase().includes(mostGoalsMatchQuery.trim().toLowerCase())
        );
    if (mostGoalsMatchCountryFilter !== 'all') {
      list = list.filter((t) => {
        const code =
          t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : '');
        return code === mostGoalsMatchCountryFilter;
      });
    }
    return list;
  }, [isMostGoalsMatchAutoFilled, championTeams, mostGoalsMatchQuery, mostGoalsMatchCountryFilter]);

  const filteredFinalAsiaTeams = useMemo(() => {
    let list = isFinalAsiaAutoFilled
      ? finalAsiaTeams
      : finalAsiaTeams.filter((t) =>
          t.name.toLowerCase().includes(finalAsiaQuery.trim().toLowerCase())
        );
    if (finalAsiaCountryFilter !== 'all') {
      list = list.filter((t) => {
        const code =
          t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : '');
        return code === finalAsiaCountryFilter;
      });
    }
    return list;
  }, [isFinalAsiaAutoFilled, finalAsiaTeams, finalAsiaQuery, finalAsiaCountryFilter]);

  const selectedMostGoalsTeam =
    championTeams.find((t) => String(t.id) === String(selectedMostGoalsMatchId)) || null;

  const selectedFinalAsiaTeam =
    finalAsiaTeams.find((t) => String(t.id) === String(selectedFinalAsiaTeamId)) || null;

  const isVoteSubmitting =
    isChampionVoting ||
    isTopScorerVoting ||
    isBestPlayerVoting ||
    isFirstGoalScorerVoting ||
    isFinalAsiaVoting ||
    isMostGoalsMatchVoting;

  const closeVoteConfirm = () => {
    if (isVoteSubmitting) return;
    setVoteConfirm({
      open: false,
      type: '',
      title: '',
      selectedName: '',
      selectedImage: '',
      payload: null,
      error: ''
    });
  };

  const closeVoteSuccess = () => setVoteSuccessOpen(false);

  const closeVoteFailure = () => {
    setVoteFailureOpen(false);
    setVoteFailureMessage('');
  };

  const handleConfirmVote = async () => {
    if (!voteConfirm?.type || !voteConfirm?.payload) return;
    if (SKIP_WORLD_CUP_VOTE_API_FOR_UI) {
      closeVoteConfirm();
      if (SKIP_WORLD_CUP_VOTE_UI_SHOW_FAILURE) {
        setVoteFailureMessage(
          'Gửi dự đoán không thành công. Vui lòng kiểm tra lại hoặc thử sau. (Nội dung mẫu khi tắt API.)'
        );
        setVoteFailureOpen(true);
      } else {
        setVoteSuccessOpen(true);
      }
      return;
    }
    if (voteConfirm.type === 'champion') {
      let voteSucceeded = false;
      let failureMsg = '';
      setIsChampionVoting(true);
      try {
        const res = await voteWorldCupChampion(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;
        if (ok) {
          const championsRes = await getTeamsChampions();
          const rawChampions =
            championsRes?.code === 0 && Array.isArray(championsRes?.data)
              ? championsRes.data
              : unwrapApiList(championsRes);
          const championRows = rawChampions
            .map((row, i) => mapTeamsChampionsRow(row, i))
            .filter(Boolean)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0));
          if (championRows.length) setChampionLeaderboard(championRows);
          voteSucceeded = true;
        } else {
          failureMsg = res?.text_mess || res?.message || 'Không thể bình chọn đội vô địch.';
        }
      } catch (e) {
        failureMsg = e?.message || 'Không thể bình chọn đội vô địch.';
      } finally {
        setIsChampionVoting(false);
      }
      if (voteSucceeded) {
        closeVoteConfirm();
        setVoteSuccessOpen(true);
      } else if (failureMsg) {
        closeVoteConfirm();
        setVoteFailureMessage(failureMsg);
        setVoteFailureOpen(true);
      }
      return;
    }

    if (voteConfirm.type === 'top-scorer') {
      let voteSucceeded = false;
      let failureMsg = '';
      setIsTopScorerVoting(true);
      try {
        const res = await voteWorldCupTopScorer(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;
        if (ok) {
          const topScorerPredRes = await getTopScorerPredictions({
            params: { site: WC_SITE }
          });
          const rawTopScorer =
            topScorerPredRes?.code === 0 && Array.isArray(topScorerPredRes?.data)
              ? topScorerPredRes.data
              : unwrapApiList(topScorerPredRes);
          const topScorerRows = rawTopScorer
            .map((row, i) => {
              const m = mapTopScorerPredictionRow(row, i);
              return m ? { m, i } : null;
            })
            .filter(Boolean)
            .sort((a, b) => (b.m.votes || 0) - (a.m.votes || 0))
            .slice(0, 10)
            .map((x) => x.m);
          if (topScorerRows.length) setTopScorerLeaderboard(topScorerRows);
          voteSucceeded = true;
        } else {
          failureMsg = res?.text_mess || res?.message || 'Không thể bình chọn.';
        }
      } catch (e) {
        failureMsg = e?.message || 'Không thể bình chọn.';
      } finally {
        setIsTopScorerVoting(false);
      }
      if (voteSucceeded) {
        closeVoteConfirm();
        setVoteSuccessOpen(true);
      } else if (failureMsg) {
        closeVoteConfirm();
        setVoteFailureMessage(failureMsg);
        setVoteFailureOpen(true);
      }
      return;
    }

    if (voteConfirm.type === 'best-player') {
      let voteSucceeded = false;
      let failureMsg = '';
      setIsBestPlayerVoting(true);
      try {
        const res = await voteWorldCupBestPlayer(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;
        if (ok) {
          const bestPlayerPredRes = await getBestPlayerPredictions({
            params: { site: WC_SITE }
          });
          const rawBestPlayer =
            bestPlayerPredRes?.code === 0 && Array.isArray(bestPlayerPredRes?.data)
              ? bestPlayerPredRes.data
              : unwrapApiList(bestPlayerPredRes);
          const bestPlayerRows = rawBestPlayer
            .map((row, i) => {
              const m = mapTopScorerPredictionRow(row, i);
              return m ? { m, i } : null;
            })
            .filter(Boolean)
            .sort((a, b) => (b.m.votes || 0) - (a.m.votes || 0))
            .slice(0, 10)
            .map((x) => x.m);
          if (bestPlayerRows.length) setBestPlayerLeaderboard(bestPlayerRows);
          voteSucceeded = true;
        } else {
          failureMsg = res?.text_mess || res?.message || 'Không thể bình chọn.';
        }
      } catch (e) {
        failureMsg = e?.message || 'Không thể bình chọn.';
      } finally {
        setIsBestPlayerVoting(false);
      }
      if (voteSucceeded) {
        closeVoteConfirm();
        setVoteSuccessOpen(true);
      } else if (failureMsg) {
        closeVoteConfirm();
        setVoteFailureMessage(failureMsg);
        setVoteFailureOpen(true);
      }
      return;
    }

    if (voteConfirm.type === 'final-asia-team') {
      let voteSucceeded = false;
      let failureMsg = '';
      setIsFinalAsiaVoting(true);
      try {
        const res = await voteWorldCupFinalAsiaTeam(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;
        if (ok) {
          const finalAsiaRes = await getFinalAsiaTeamPredictions({
            params: { site: WC_SITE }
          });
          const rawFinal =
            finalAsiaRes?.code === 0 && Array.isArray(finalAsiaRes?.data)
              ? finalAsiaRes.data
              : unwrapApiList(finalAsiaRes);
          const finalRows = rawFinal
            .map((row, i) => mapTeamsChampionsRow(row, i))
            .filter(Boolean)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0));
          if (finalRows.length) setFinalAsiaLeaderboard(finalRows);
          voteSucceeded = true;
        } else {
          failureMsg = res?.text_mess || res?.message || 'Không thể gửi dự đoán.';
        }
      } catch (e) {
        failureMsg = e?.message || 'Không thể gửi dự đoán.';
      } finally {
        setIsFinalAsiaVoting(false);
      }
      if (voteSucceeded) {
        closeVoteConfirm();
        setVoteSuccessOpen(true);
      } else if (failureMsg) {
        closeVoteConfirm();
        setVoteFailureMessage(failureMsg);
        setVoteFailureOpen(true);
      }
      return;
    }

    if (voteConfirm.type === 'most-goals-match') {
      let voteSucceeded = false;
      let failureMsg = '';
      setIsMostGoalsMatchVoting(true);
      try {
        const res = await voteWorldCupMostGoalsInMatch(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;
        if (ok) {
          const mostGoalsRes = await getMostGoalsInMatchPredictions({
            params: { site: WC_SITE }
          });
          const rawMost =
            mostGoalsRes?.code === 0 && Array.isArray(mostGoalsRes?.data)
              ? mostGoalsRes.data
              : unwrapApiList(mostGoalsRes);
          const mostRows = rawMost
            .map((row, i) => mapTeamsChampionsRow(row, i))
            .filter(Boolean)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0));
          if (mostRows.length) setMostGoalsMatchLeaderboard(mostRows);
          voteSucceeded = true;
        } else {
          failureMsg = res?.text_mess || res?.message || 'Không thể gửi dự đoán.';
        }
      } catch (e) {
        failureMsg = e?.message || 'Không thể gửi dự đoán.';
      } finally {
        setIsMostGoalsMatchVoting(false);
      }
      if (voteSucceeded) {
        closeVoteConfirm();
        setVoteSuccessOpen(true);
      } else if (failureMsg) {
        closeVoteConfirm();
        setVoteFailureMessage(failureMsg);
        setVoteFailureOpen(true);
      }
    }
  };

  const renderChampionTab = () => {
    const onSubmitChampion = () => {
      if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
        dispatchOpenLoginModal();
        return;
      }
      if (!selectedChampion) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 đội để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({ message: 'Cảnh báo', description: 'Đang tải thông tin IP/FP.' });
        return;
      }
      setVoteConfirm({
        open: true,
        type: 'champion',
        title: 'Đội vô địch',
        selectedName: selectedChampion.name,
        selectedImage: selectedChampion.flag || '',
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: WC_SITE,
          teamName: selectedChampion.name,
          teamId: selectedChampion.id
        },
        error: ''
      });
    };
    return (
      <ChampionPredictionTab
        championLeaderboard={championLeaderboard}
        championTeams={championTeams}
        filteredChampionTeams={filteredChampionTeams}
        championQuery={championQuery}
        setChampionQuery={(value) => {
          setIsChampionAutoFilled(false);
          setChampionQuery(value);
        }}
        championCountryFilter={championCountryFilter}
        setChampionCountryFilter={setChampionCountryFilter}
        championCountryOptions={championCountryOptions}
        isChampionVoting={isChampionVoting}
        onSubmitChampion={onSubmitChampion}
        selectedChampionId={selectedChampionId}
        setSelectedChampionId={(id) => {
          setSelectedChampionId(id);
          const team = championTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setChampionQuery(team.name);
            setIsChampionAutoFilled(true);
          }
        }}
        fillChampionQueryFromPick={(name) => {
          setIsChampionAutoFilled(true);
          setChampionQuery(name);
        }}
      />
    );
  };

  const renderTopScorerTab = () => {
    const onSubmitTopScorer = () => {
      if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
        dispatchOpenLoginModal();
        return;
      }
      if (!selectedTopScorer) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 cầu thủ để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({ message: 'Cảnh báo', description: 'Đang tải thông tin IP/FP.' });
        return;
      }
      setVoteConfirm({
        open: true,
        type: 'top-scorer',
        title: 'Vua phá lưới',
        selectedName: selectedTopScorer.name,
        selectedImage: selectedTopScorer.photo || '',
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: WC_SITE,
          playerName: selectedTopScorer.name,
          teamName: selectedTopScorer.teamName,
          playerId: selectedTopScorer.id
        },
        error: ''
      });
    };
    return (
      <PlayerPredictionTab
        title="Dự đoán vua phá lưới"
        leaderboard={topScorerLeaderboard}
        players={filteredTopScorerPlayers}
        catalogPlayers={topScorerPlayersList}
        query={topScorerQuery}
        setQuery={(value) => {
          setIsTopScorerAutoFilled(false);
          setTopScorerQuery(value);
        }}
        countryFilter={topScorerCountryFilter}
        setCountryFilter={setTopScorerCountryFilter}
        countryOptions={playerCountryOptions}
        selectedId={selectedTopScorerId}
        setSelectedId={(id) => {
          setSelectedTopScorerId(id);
          const player = topScorerPlayersList.find((p) => String(p.id) === String(id));
          if (player?.name) {
            setTopScorerQuery(player.name);
            setIsTopScorerAutoFilled(true);
          }
        }}
        loading={isTopScorerVoting}
        onSubmit={onSubmitTopScorer}
        fillPlayerQueryFromPick={(name) => {
          setIsTopScorerAutoFilled(true);
          setTopScorerQuery(name);
        }}
      />
    );
  };

  const renderBestPlayerTab = () => {
    const onSubmitBestPlayer = () => {
      if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
        dispatchOpenLoginModal();
        return;
      }
      if (!selectedBestPlayer) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 cầu thủ để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({ message: 'Cảnh báo', description: 'Đang tải thông tin IP/FP.' });
        return;
      }
      setVoteConfirm({
        open: true,
        type: 'best-player',
        title: 'Cầu thủ xuất sắc nhất',
        selectedName: selectedBestPlayer.name,
        selectedImage: selectedBestPlayer.photo || '',
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: WC_SITE,
          playerName: selectedBestPlayer.name,
          teamName: selectedBestPlayer.teamName,
          playerId: selectedBestPlayer.id
        },
        error: ''
      });
    };
    return (
      <PlayerPredictionTab
        title="Dự đoán cầu thủ xuất sắc nhất"
        leaderboard={bestPlayerLeaderboard}
        players={filteredBestPlayerPlayers}
        catalogPlayers={topScorerPlayersList}
        query={bestPlayerQuery}
        setQuery={(value) => {
          setIsBestPlayerAutoFilled(false);
          setBestPlayerQuery(value);
        }}
        countryFilter={bestPlayerCountryFilter}
        setCountryFilter={setBestPlayerCountryFilter}
        countryOptions={playerCountryOptions}
        selectedId={selectedBestPlayerId}
        setSelectedId={(id) => {
          setSelectedBestPlayerId(id);
          const player = topScorerPlayersList.find((p) => String(p.id) === String(id));
          if (player?.name) {
            setBestPlayerQuery(player.name);
            setIsBestPlayerAutoFilled(true);
          }
        }}
        loading={isBestPlayerVoting}
        onSubmit={onSubmitBestPlayer}
        fillPlayerQueryFromPick={(name) => {
          setIsBestPlayerAutoFilled(true);
          setBestPlayerQuery(name);
        }}
      />
    );
  };

  const renderFinalAsiaTeamTab = () => {
    const onSubmitFinalAsia = () => {
      if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
        dispatchOpenLoginModal();
        return;
      }
      if (!selectedFinalAsiaTeam) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 đội để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({ message: 'Cảnh báo', description: 'Đang tải thông tin IP/FP.' });
        return;
      }
      setVoteConfirm({
        open: true,
        type: 'final-asia-team',
        title: 'Đội Châu Á vào sâu nhất',
        selectedName: selectedFinalAsiaTeam.name,
        selectedImage: selectedFinalAsiaTeam.flag || '',
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: WC_SITE,
          teamName: selectedFinalAsiaTeam.name,
          teamId: selectedFinalAsiaTeam.id
        },
        error: ''
      });
    };
    return (
      <ChampionPredictionTab
        championLeaderboard={finalAsiaLeaderboard}
        championTeams={finalAsiaTeams}
        filteredChampionTeams={filteredFinalAsiaTeams}
        championQuery={finalAsiaQuery}
        setChampionQuery={(value) => {
          setIsFinalAsiaAutoFilled(false);
          setFinalAsiaQuery(value);
        }}
        championCountryFilter={finalAsiaCountryFilter}
        setChampionCountryFilter={setFinalAsiaCountryFilter}
        championCountryOptions={championCountryOptions}
        isChampionVoting={isFinalAsiaVoting}
        onSubmitChampion={onSubmitFinalAsia}
        selectedChampionId={selectedFinalAsiaTeamId}
        setSelectedChampionId={(id) => {
          setSelectedFinalAsiaTeamId(id);
          const team = finalAsiaTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setFinalAsiaQuery(team.name);
            setIsFinalAsiaAutoFilled(true);
          }
        }}
        votePanelTitle="Đội Châu Á vào sâu nhất"
        searchPlaceholder="Tìm đội Châu Á..."
        fillChampionQueryFromPick={(name) => {
          setIsFinalAsiaAutoFilled(true);
          setFinalAsiaQuery(name);
        }}
      />
    );
  };

  const renderMostGoalsMatchTab = () => {
    const onSubmitMostGoals = () => {
      if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
        dispatchOpenLoginModal();
        return;
      }
      if (!selectedMostGoalsTeam) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 đội để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({ message: 'Cảnh báo', description: 'Đang tải thông tin IP/FP.' });
        return;
      }
      setVoteConfirm({
        open: true,
        type: 'most-goals-match',
        title: 'Đội ghi nhiều bàn nhất trong 1 trận',
        selectedName: selectedMostGoalsTeam.name,
        selectedImage: selectedMostGoalsTeam.flag || '',
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: WC_SITE,
          teamName: selectedMostGoalsTeam.name,
          teamId: selectedMostGoalsTeam.id
        },
        error: ''
      });
    };
    return (
      <ChampionPredictionTab
        championLeaderboard={mostGoalsMatchLeaderboard}
        championTeams={championTeams}
        filteredChampionTeams={filteredMostGoalsTeams}
        championQuery={mostGoalsMatchQuery}
        setChampionQuery={(value) => {
          setIsMostGoalsMatchAutoFilled(false);
          setMostGoalsMatchQuery(value);
        }}
        championCountryFilter={mostGoalsMatchCountryFilter}
        setChampionCountryFilter={setMostGoalsMatchCountryFilter}
        championCountryOptions={championCountryOptions}
        isChampionVoting={isMostGoalsMatchVoting}
        onSubmitChampion={onSubmitMostGoals}
        selectedChampionId={selectedMostGoalsMatchId}
        setSelectedChampionId={(id) => {
          setSelectedMostGoalsMatchId(id);
          const team = championTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setMostGoalsMatchQuery(team.name);
            setIsMostGoalsMatchAutoFilled(true);
          }
        }}
        votePanelTitle="Dự đoán đội ghi nhiều bàn nhất trong 1 trận"
        searchPlaceholder="Tìm kiếm đội tuyển"
        fillChampionQueryFromPick={(name) => {
          setIsMostGoalsMatchAutoFilled(true);
          setMostGoalsMatchQuery(name);
        }}
      />
    );
  };

  return (
    <div className="w-full h-full flex flex-col">
      <img
        src={bg_home}
        alt="Background"
        className="w-full h-full fixed right-0 bottom-0 object-cover -z-10"
      />
      <div className="relative z-10">
        {/* <Header /> */}
        <HeaderMenu />
        <div className="mx-auto mt-2 w-full max-w-[1196px] px-2">
          <div
            className="relative z-20 w-full overflow-x-auto bg-white rounded-t-[14px]"
            style={{ background: '#FFFFFF' }}
          >
            <div className="flex items-stretch justify-start">
              {predictionTabs.map((tab, index) => {
                const isActive = activePredictionTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActivePredictionTab(tab.key)}
                    className={twMerge(
                      'shrink-0 h-[42px] md:h-[48px] px-2 md:px-3 transition-all duration-200 border-0 outline-none',
                      'flex items-center justify-center gap-2 whitespace-nowrap',
                      isActive ? 'bg-white shadow-[inset_0_-4px_0_#0A5AE1]' : 'bg-white'
                    )}
                  >
                    <img
                      src={tab.icon}
                      alt=""
                      className="h-[20px] md:h-[25px] w-auto object-contain shrink-0"
                    />
                    <span
                      className={twMerge(
                        'font-roboto text-[12px] md:text-[13px] font-extrabold uppercase leading-none tracking-[-0.02em]',
                        isActive ? 'text-[#0A5AE1]' : 'text-[#1E56C7]'
                      )}
                    >
                      {tab.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mx-auto w-full md:pb-2 rounded-t-none px-2">
          {/* <div className="flex space-x-6 mt-1 lg:mt-3 justify-center items-center">
            <Button
              type="link"
              icon={<LeftOutlined />}
              onClick={scrollLeft}
              className="text-[#26A9E0] !px-0 !mx-0"
            />
            <div
              className="flex overflow-x-auto scrollbar-custom pt-1 pb-2 !mx-0"
              ref={leagueListRef}
              style={{ scrollBehavior: 'smooth' }}
            >
              {leagues.map((league) => (
                <div
                  key={league.id}
                  className={twMerge(
                    'flex justify-center items-center p-1 mx-2 rounded-lg cursor-pointer transition-all duration-300 hover:scale-105',
                    selectedLeagues.includes(league.id)
                      ? 'bg-black/15 shadow-lg shadow-[#26A9E0] border-2 border-[#26A9E0]'
                      : 'bg-white/80 backdrop-blur-sm border-2 border-[#26A9E0]/30 hover:border-[#26A9E0] hover:shadow-md'
                  )}
                  style={{ width: '80px', minWidth: '80px', border: '1px solid #26A9E0' }}
                  onClick={() => handleLeagueClick(league.id)}
                >
                  <img src={league.logo} alt={league.name} className="h-12 object-contain" />
                </div>
              ))}
            </div>
            <Button
              type="link"
              icon={<RightOutlined />}
              onClick={scrollRight}
              className="text-[#26A9E0] !px-0 !mx-0"
            />
          </div> */}
          {/* <div className="w-full h-auto my-3 md:my-2">
            <div className="flex flex-wrap gap-1 sm:gap-3 lg:gap-5 items-center justify-center w-full">
              <button
                onClick={() => setActiveTab('all')}
                className={`h-[26px] sm:h-[36px] lg:h-[40px] px-2 sm:px-4 lg:px-6 py-1 rounded-[12px] sm:rounded-[18px] lg:rounded-[25px] text-[11px] lg:text-[15px] font-normal ${
                  activeTab === 'all' ? 'text-white' : 'text-[#3C3C3C]'
                } transition-all duration-200 outline-none focus:outline-none capitalize border-solid border-2 border-[#26A9E0] ${
                  activeTab === 'all' ? 'bg-[#26A9E0]' : 'bg-[#FFF]'
                }`}
              >
                Tất Cả
              </button>
              <button
                onClick={() => setActiveTab('today')}
                className={`h-[26px] sm:h-[36px] lg:h-[40px] px-2 sm:px-4 lg:px-6 py-1 rounded-[12px] sm:rounded-[18px] lg:rounded-[25px] text-[11px] lg:text-[15px] font-normal ${
                  activeTab === 'today' ? 'text-white' : 'text-[#3C3C3C]'
                } transition-all duration-200 border-solid border-2 border-[#26A9E0] outline-none focus:outline-none capitalize ${
                  activeTab === 'today' ? 'bg-[#26A9E0]' : 'bg-[#FFF]'
                }`}
              >
                Hôm Nay
              </button>
              <button
                onClick={() => setActiveTab('tomorrow')}
                className={`h-[26px] sm:h-[36px] lg:h-[40px] px-2 sm:px-4 lg:px-6 py-1 rounded-[12px] sm:rounded-[18px] lg:rounded-[25px] text-[11px] lg:text-[15px] font-normal ${
                  activeTab === 'tomorrow' ? 'text-white' : 'text-[#3C3C3C]'
                } transition-all duration-200 border-solid border-2 border-[#26A9E0] outline-none focus:outline-none capitalize ${
                  activeTab === 'tomorrow' ? 'bg-[#26A9E0]' : 'bg-[#FFF]'
                }`}
              >
                Ngày Mai
              </button>
            </div>
          </div> */}
          <div className="w-full h-full flex flex-col">
            {activePredictionTab === 'match' ? (
              <>
                <Form
                  name="formMatch"
                  onFinish={onFinishForm}
                  onFinishFailed={onFinishFailed}
                  autoComplete="off"
                  form={form}
                  initialValues={{ account: '' }}
                >
                  <div className="w-full h-full">
                    <div
                      className="flex flex-col max-w-[1180px] mx-auto w-full h-full items-center mt-0 pb-6"
                      style={{
                        background:
                          'linear-gradient(0deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), rgba(255, 255, 255, 0.5)',
                        backgroundBlendMode: 'normal, overlay',
                        boxShadow:
                          '0px 0px 22.2698px #92DEFF, 0px 8px 12px rgba(0, 0, 0, 0.08), inset 2.02822px 2.02822px 2.02822px -2.02822px #FFFFFF, inset -2.02822px -2.02822px 2.02822px -2.02822px #FFFFFF',
                        backdropFilter: 'blur(10.1411px)',
                        WebkitBackdropFilter: 'blur(10.1411px)',
                        borderRadius: '0 0 14.0625px 14.0625px'
                      }}
                    >
                      <div className="flex flex-col p-2 py-5 w-full md:px-6 h-full items-center">
                        <div className="hidden flex flex-col sm:flex-row w-full h-full items-center px-3 justify-between gap-2 md:mx-3">
                          <div className="flex gap-2 md:py-4">
                            <Form.Item name="account" className="mb-0">
                              <Input
                                placeholder="Tên đăng nhập ..."
                                maxLength={20}
                                prefix={<UserOutlined />}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Vui lòng điền tên đăng nhập!'
                                  }
                                ]}
                              />
                            </Form.Item>
                            <Form.Item className="mb-0">
                              <Button
                                type="primary"
                                htmlType="submit"
                                loading={isLoadingSubmit}
                                className="text-white bg-[#0053DF] font-medium rounded-[4.286px] border-0"
                                // style={{
                                //   background: 'linear-gradient(180deg, #53ADFF 0%, #0066FF 100%)'
                                // }}
                              >
                                Xác Nhận
                              </Button>
                            </Form.Item>
                          </div>
                          <div className="flex gap-2 md:py-4">
                            <Button
                              type="primary"
                              className="text-white bg-[#0053DF] font-medium rounded-[12px] border-0"
                              onClick={() =>
                                window.open('https://ls47.live/', '_blank', 'noopener,noreferrer')
                              }
                              // style={{
                              //   background: 'linear-gradient(180deg, #53ADFF 0%, #0066FF 100%)'
                              // }}
                            >
                              Xem live
                            </Button>
                          </div>
                        </div>
                        {/* <p className="w-full mt-3 px-3 text-black text-sm md:text-base font-normal italic">
                          Nhập tên đăng nhập và tỷ số ít nhất 1 trận để dự đoán.
                        </p> */}

                        <div className="hidden flex w-full h-full mt-2 items-center px-3 justify-between gap-2">
                          <Button
                            type="primary"
                            onClick={() => setIsModalHistory(true)}
                            className="text-white bg-[#0053DF] font-medium rounded-[4.286px] border-0"
                            // style={{
                            //   background: 'linear-gradient(180deg, #53ADFF 0%, #0066FF 100%)'
                            // }}
                          >
                            Lịch sử dự đoán
                          </Button>
                          <Button
                            type="primary"
                            icon={<GiftOutlined />}
                            onClick={() => setIsModalRule(true)}
                            className="text-white bg-[#0053DF] font-medium rounded-[4.286px] border-0"
                          >
                            Cơ cấu giải thưởng
                          </Button>
                        </div>
                      </div>

                      {isLoading ? (
                        <div className="w-full h-full text-center py-8">
                          <Spin />
                        </div>
                      ) : predictableMatches.length <= 0 ? (
                        <div className="flex w-full items-center justify-center py-6">
                          <p className="text-center">Hiện tại không có trận đấu nào để dự đoán</p>
                        </div>
                      ) : (
                        <div className="mt-1 md:-mt-2 w-full px-1 md:px-2">
                          <div className="rounded-2xl p-[2px] outline outline-2 outline-[#A6CAFA]">
                            <div className="rounded-[14px] flex flex-col overflow-hidden bg-[#fafbff]">
                              <div className="flex flex-col gap-2 p-2 md:p-3">
                                {paginatedMatches.map((item) => renderMatchCard(item))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2"></div>
                  </div>
                </Form>

                {/* <div
                  className="flex flex-col max-w-[1180px] mx-auto w-full h-full items-center mt-0"
                  style={{
                    background:
                      'linear-gradient(0deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), rgba(255, 255, 255, 0.5)',
                    backgroundBlendMode: 'normal, overlay',
                    boxShadow:
                      '0px 0px 22.2698px #92DEFF, 0px 8px 12px rgba(0, 0, 0, 0.08), inset 2.02822px 2.02822px 2.02822px -2.02822px #FFFFFF, inset -2.02822px -2.02822px 2.02822px -2.02822px #FFFFFF',
                    backdropFilter: 'blur(10.1411px)',
                    WebkitBackdropFilter: 'blur(10.1411px)',
                  }}
                >
                  <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl">
                    <div className="flex items-center justify-center px-4 pt-4">
                      <h2 className="text-xl font-bold text-[#000]">LỊCH THI ĐẤU HOT</h2>
                    </div>
                    <div className="p-4">
                      <iframe
                        src="https://football-widget.pages.dev/"
                        width="100%"
                        height="600"
                        frameBorder="0"
                        allowFullScreen
                        title="Football Widget"
                        className="w-full rounded-lg"
                      />
                    </div>
                  </div>
                </div> */}
              </>
            ) : (
              <div
                className="flex flex-col max-w-[1180px] mx-auto w-full items-center mt-0 rounded-b-[14px] overflow-hidden opacity-100"
                style={{
                  background:
                    'linear-gradient(0deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), rgba(255, 255, 255, 0.5)',
                  backgroundBlendMode: 'normal, overlay',
                  boxShadow:
                    '0px 0px 22.2698px #92DEFF, 0px 8px 12px rgba(0, 0, 0, 0.08), inset 2.02822px 2.02822px 2.02822px -2.02822px #FFFFFF, inset -2.02822px -2.02822px 2.02822px -2.02822px #FFFFFF',
                  backdropFilter: 'blur(10.1411px)',
                  WebkitBackdropFilter: 'blur(10.1411px)',
                  borderRadius: '0 0 14.0625px 14.0625px'
                }}
              >
                <div className="w-full ">
                  <div className="rounded-2xl  ">
                    {activePredictionTab === 'champion' ? (
                      renderChampionTab()
                    ) : activePredictionTab === 'top-scorer' ? (
                      renderTopScorerTab()
                    ) : activePredictionTab === 'best-player' ? (
                      renderBestPlayerTab()
                    ) : activePredictionTab === 'final-asia-team' ? (
                      renderFinalAsiaTeamTab()
                    ) : activePredictionTab === 'most-goals-match' ? (
                      renderMostGoalsMatchTab()
                    ) : (
                      <div className="w-full py-12 flex flex-col items-center justify-center text-[#0053DF] font-semibold">
                        Nội dung tab "
                        {predictionTabs.find((t) => t.key === activePredictionTab)?.label}" đang
                        được cập nhật.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {voteConfirm.open ? (
        <div className="fixed inset-0 z-[1050] flex items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-black/45"
            aria-hidden
            onClick={isVoteSubmitting ? undefined : closeVoteConfirm}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="vote-confirm-title"
            className="relative z-[1] w-full max-w-[360px] overflow-hidden rounded-2xl border border-white/90 shadow-2xl lg:max-w-[480px] lg:rounded-3xl xl:max-w-[520px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="vote-confirm-title" className="sr-only">
              Xác nhận bình chọn {voteConfirm.title}
            </h2>
            <div className="relative">
              <button
                type="button"
                onClick={closeVoteConfirm}
                disabled={isVoteSubmitting}
                className="absolute right-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-[#1f1f1f] bg-gradient-to-b from-[#ff6b6b] via-[#e53935] to-[#b71c1c] text-white shadow-[inset_0_2px_5px_rgba(255,255,255,0.55),inset_0_-6px_14px_rgba(0,0,0,0.28),0_0_0_2px_#eceff1,0_0_0_3px_#78909c,0_4px_12px_rgba(0,0,0,0.45)] transition-[filter,transform] hover:brightness-[1.06] active:scale-[0.96] active:brightness-[0.96] disabled:pointer-events-none disabled:opacity-50 sm:right-3 sm:top-3 sm:h-10 sm:w-10 lg:right-4 lg:top-4 lg:h-8 lg:w-8"
                aria-label="Đóng"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="h-[46%] w-[46%] sm:h-[44%] sm:w-[44%]"
                  aria-hidden
                >
                  <path
                    d="M7 7l10 10M17 7L7 17"
                    stroke="currentColor"
                    strokeWidth="2.35"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <div
                className="relative w-full overflow-hidden bg-[#c5e3f7] bg-cover bg-center bg-no-repeat aspect-[888/707]"
                style={{ backgroundImage: 'url(/assets/images/tab/popup.png)' }}
              >
                <div className="pointer-events-none absolute max-[400px]:left-1/2 max-[400px]:top-[3%] max-[400px]:w-[40%] left-1/2 top-[5%] z-[5] w-[min(58%,200px)] max-w-[220px] -translate-x-1/2 sm:top-[6%] sm:max-w-[130px] lg:top-[5.5%] lg:max-w-[276px] xl:max-w-[300px] aspect-[372/450]">
                  <div className="absolute max-[400px]:left-1/2 max-[400px]:top-[45%] max-[400px]:w-[65%] left-1/2 top-[31%] z-0 aspect-square w-[41%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-white shadow-[inset_0_1px_6px_rgba(0,0,0,0.12)] sm:top-[46%] sm:w-[60%] lg:top-[47%] lg:w-[62%]">
                    {voteConfirm.selectedImage ? (
                      <img
                        src={voteConfirm.selectedImage}
                        alt=""
                        className={twMerge(
                          'block h-full w-full object-cover object-center',
                          voteConfirm.type === 'champion' ||
                          voteConfirm.type === 'final-asia-team' ||
                          voteConfirm.type === 'most-goals-match'
                            ? 'origin-center scale-[1.28] sm:scale-[1.22] lg:scale-[1.18]'
                            : ''
                        )}
                        draggable={false}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-[#e8eef4] text-[11px] font-medium text-[#94a3b8]">
                        —
                      </div>
                    )}
                  </div>
                  <img
                    src="/assets/images/tab/khung.png"
                    alt=""
                    className="pointer-events-none absolute inset-0 z-10 h-full w-full select-none object-contain object-top"
                    draggable={false}
                  />
                </div>

                <div className="pointer-events-auto absolute inset-x-0 bottom-0 z-[15] flex flex-col gap-2 bg-gradient-to-t from-[#b8d9f0] from-[25%] via-[#d0e5f7]/[0.96] to-transparent px-3 pb-3 pt-8 sm:px-4 sm:pb-4 sm:pt-10 lg:gap-3 lg:px-5 lg:pb-5 lg:pt-12">
                  <p className="text-center text-[12px] leading-snug text-[#2a2a2a] sm:text-[14px] sm:leading-relaxed lg:text-[15px] lg:leading-relaxed xl:text-base">
                    Bạn đã bình chọn{' '}
                    <span className="font-bold text-[#2a2a2a]">{voteConfirm.selectedName}</span> là{' '}
                    <span className="font-semibold text-[#2a2a2a]">
                      {voteConfirm.type === 'champion'
                        ? 'đội vô địch'
                        : voteConfirm.type === 'top-scorer'
                          ? 'Vua Phá Lưới'
                          : voteConfirm.type === 'best-player'
                            ? 'Cầu thủ xuất sắc nhất'
                            : voteConfirm.type === 'first-goal-scorer'
                              ? 'Cầu thủ ghi bàn đầu tiên'
                              : voteConfirm.type === 'final-asia-team'
                                ? 'đội Châu Á vào sâu nhất'
                                : voteConfirm.type === 'most-goals-match'
                                  ? 'đội ghi nhiều bàn nhất trong 1 trận'
                                  : voteConfirm.title}
                    </span>{' '}
                    World Cup 2026. Bạn có chắc chắn không?
                  </p>

                  {voteConfirm.error ? (
                    <div className="rounded-lg border border-[#f1b2b2] bg-[#fff1f0] px-2 py-1.5 text-center text-[11px] text-[#5f2120] sm:text-xs lg:text-sm lg:py-2">
                      {voteConfirm.error}
                    </div>
                  ) : null}

                  <div className="flex flex-row flex-wrap items-center justify-center gap-2 sm:gap-3 lg:gap-4">
                    <button
                      type="button"
                      onClick={closeVoteConfirm}
                      disabled={isVoteSubmitting}
                      className="w-[min(46%,9.75rem)] max-w-[200px] shrink-0 border-0 bg-transparent p-0 shadow-none outline-none transition-opacity hover:opacity-92 active:opacity-88 disabled:pointer-events-none disabled:opacity-45 focus-visible:ring-2 focus-visible:ring-[#90a4ae] focus-visible:ring-offset-2 focus-visible:ring-offset-[#d0e5f7]"
                      aria-label="Hủy bỏ"
                    >
                      <img
                        src="/assets/images/tab/btn-huybo.png"
                        alt=""
                        width={265}
                        height={83}
                        className="pointer-events-none block h-auto w-full select-none object-contain"
                        draggable={false}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmVote}
                      disabled={isVoteSubmitting}
                      aria-busy={isVoteSubmitting}
                      aria-label={isVoteSubmitting ? 'Đang gửi' : 'Xác nhận'}
                      className="w-[min(46%,9.75rem)] max-w-[200px] shrink-0 border-0 bg-transparent p-0 shadow-none outline-none transition-opacity hover:opacity-92 active:opacity-88 disabled:pointer-events-none disabled:opacity-55 focus-visible:ring-2 focus-visible:ring-[#0253e0] focus-visible:ring-offset-2 focus-visible:ring-offset-[#d0e5f7]"
                    >
                      <img
                        src="/assets/images/tab/btn-xacnhan.png"
                        alt=""
                        width={265}
                        height={83}
                        className="pointer-events-none block h-auto w-full select-none object-contain"
                        draggable={false}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {voteSuccessOpen ? (
        <div className="fixed inset-0 z-[1060] flex items-center justify-center px-4 py-6">
          <div className="absolute inset-0 bg-black/45" aria-hidden onClick={closeVoteSuccess} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="vote-success-title"
            className="relative z-[1] w-full min-w-0 max-w-[360px] overflow-hidden rounded-2xl border border-white/90 shadow-2xl lg:max-w-[420px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="vote-success-title" className="sr-only">
              DỰ ĐOÁN THÀNH CÔNG
            </h2>
            <div className="relative">
              <button
                type="button"
                onClick={closeVoteSuccess}
                className="absolute right-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-[#1f1f1f] bg-gradient-to-b from-[#ff6b6b] via-[#e53935] to-[#b71c1c] text-white shadow-[inset_0_2px_5px_rgba(255,255,255,0.55),inset_0_-6px_14px_rgba(0,0,0,0.28),0_0_0_2px_#eceff1,0_0_0_3px_#78909c,0_4px_12px_rgba(0,0,0,0.45)] transition-[filter,transform] hover:brightness-[1.06] active:scale-[0.96] active:brightness-[0.96] sm:right-3 sm:top-3 sm:h-10 sm:w-10"
                aria-label="Đóng"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="h-[46%] w-[46%] sm:h-[44%] sm:w-[44%]"
                  aria-hidden
                >
                  <path
                    d="M7 7l10 10M17 7L7 17"
                    stroke="currentColor"
                    strokeWidth="2.35"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <div
                className="relative overflow-hidden px-6 pb-7 pt-12 sm:px-8 sm:pb-8 sm:pt-14"
                style={{
                  background: 'linear-gradient(180deg, #c5e3f7 0%, #dceef9 42%, #f0f7fc 100%)'
                }}
              >
                <div
                  className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl opacity-[0.4]"
                  aria-hidden
                >
                  <div className="absolute left-[6%] top-[10%] h-20 w-20 rounded-full bg-white/50 blur-2xl" />
                  <div className="absolute right-[8%] top-[18%] h-14 w-14 rounded-full bg-white/45 blur-xl" />
                  <div className="absolute left-[12%] bottom-[28%] h-12 w-12 rounded-full bg-white/40 blur-lg" />
                  <div className="absolute right-[14%] bottom-[22%] h-16 w-16 rounded-full bg-white/50 blur-2xl" />
                </div>

                <div className="relative z-[1] flex flex-col items-center text-center">
                  <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-[#6ee7b7] via-[#34d399] to-[#059669] shadow-[inset_0_4px_12px_rgba(255,255,255,0.45),inset_0_-6px_14px_rgba(0,0,0,0.18),0_6px_20px_rgba(5,150,105,0.35)] sm:h-[80px] sm:w-[80px]">
                    <svg
                      className="h-[38px] w-[38px] text-white sm:h-[42px] sm:w-[42px]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>

                  <p className="mt-5 font-black tracking-wide text-[#0253e0] text-[17px] leading-tight sm:text-[19px]">
                    DỰ ĐOÁN THÀNH CÔNG
                  </p>
                  <p className="mt-3 text-[14px] font-semibold leading-snug text-[#2a2a2a] sm:text-[15px]">
                    Xin chúc mừng!
                  </p>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-[#2a2a2a] sm:text-[14px]">
                    Bạn đã gửi dự đoán thành công
                  </p>

                  <button
                    type="button"
                    onClick={closeVoteSuccess}
                    className="mt-7 w-[min(88%,12rem)] max-w-[220px] shrink-0 border-0 bg-transparent p-0 shadow-none outline-none transition-opacity hover:opacity-92 active:opacity-88 focus-visible:ring-2 focus-visible:ring-[#0253e0] focus-visible:ring-offset-2 focus-visible:ring-offset-[#dceef9]"
                    aria-label="Đồng ý"
                  >
                    <img
                      src="/assets/images/tab/btn-dongy.png"
                      alt=""
                      width={265}
                      height={83}
                      className="pointer-events-none block h-auto w-full select-none object-contain"
                      draggable={false}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {voteFailureOpen ? (
        <div className="fixed inset-0 z-[1060] flex items-center justify-center px-4 py-6">
          <div className="absolute inset-0 bg-black/45" aria-hidden onClick={closeVoteFailure} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="vote-failure-title"
            aria-describedby="vote-failure-desc"
            className="relative z-[1] w-full min-w-0 max-w-[360px] overflow-hidden rounded-2xl border border-white/90 shadow-2xl lg:max-w-[420px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="vote-failure-title" className="sr-only">
              DỰ ĐOÁN KHÔNG THÀNH CÔNG
            </h2>
            <div className="relative">
              <button
                type="button"
                onClick={closeVoteFailure}
                className="absolute right-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-[#1f1f1f] bg-gradient-to-b from-[#ff6b6b] via-[#e53935] to-[#b71c1c] text-white shadow-[inset_0_2px_5px_rgba(255,255,255,0.55),inset_0_-6px_14px_rgba(0,0,0,0.28),0_0_0_2px_#eceff1,0_0_0_3px_#78909c,0_4px_12px_rgba(0,0,0,0.45)] transition-[filter,transform] hover:brightness-[1.06] active:scale-[0.96] active:brightness-[0.96] sm:right-3 sm:top-3 sm:h-10 sm:w-10"
                aria-label="Đóng"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="h-[46%] w-[46%] sm:h-[44%] sm:w-[44%]"
                  aria-hidden
                >
                  <path
                    d="M7 7l10 10M17 7L7 17"
                    stroke="currentColor"
                    strokeWidth="2.35"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <div
                className="relative overflow-hidden px-6 pb-7 pt-12 sm:px-8 sm:pb-8 sm:pt-14"
                style={{
                  background: 'linear-gradient(180deg, #c5e3f7 0%, #dceef9 42%, #f0f7fc 100%)'
                }}
              >
                <div
                  className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl opacity-[0.4]"
                  aria-hidden
                >
                  <div className="absolute left-[6%] top-[10%] h-20 w-20 rounded-full bg-white/50 blur-2xl" />
                  <div className="absolute right-[8%] top-[18%] h-14 w-14 rounded-full bg-white/45 blur-xl" />
                  <div className="absolute left-[12%] bottom-[28%] h-12 w-12 rounded-full bg-white/40 blur-lg" />
                  <div className="absolute right-[14%] bottom-[22%] h-16 w-16 rounded-full bg-white/50 blur-2xl" />
                </div>

                <div className="relative z-[1] flex flex-col items-center text-center">
                  <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-[#f87171] via-[#ef4444] to-[#b91c1c] shadow-[inset_0_4px_12px_rgba(255,255,255,0.4),inset_0_-6px_14px_rgba(0,0,0,0.22),0_6px_20px_rgba(185,28,28,0.35)] sm:h-[80px] sm:w-[80px]">
                    <svg
                      className="h-[38px] w-[38px] text-white sm:h-[42px] sm:w-[42px]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M7 7l10 10M17 7L7 17"
                      />
                    </svg>
                  </div>

                  <p className="mt-5 font-black tracking-wide text-[#c62828] text-[17px] leading-tight sm:text-[19px]">
                    DỰ ĐOÁN KHÔNG THÀNH CÔNG
                  </p>
                  <p className="mt-3 text-[14px] font-semibold leading-snug text-[#2a2a2a] sm:text-[15px]">
                    Rất tiếc!
                  </p>
                  <p
                    id="vote-failure-desc"
                    className="mt-1.5 max-w-full px-1 text-[13px] leading-relaxed text-[#2a2a2a] sm:text-[14px]"
                  >
                    {voteFailureMessage}
                  </p>

                  <button
                    type="button"
                    onClick={closeVoteFailure}
                    className="mt-7 w-[min(88%,12rem)] max-w-[220px] shrink-0 border-0 bg-transparent p-0 shadow-none outline-none transition-opacity hover:opacity-92 active:opacity-88 focus-visible:ring-2 focus-visible:ring-[#c62828] focus-visible:ring-offset-2 focus-visible:ring-offset-[#dceef9]"
                    aria-label="Đồng ý"
                  >
                    <img
                      src="/assets/images/tab/btn-dongy.png"
                      alt=""
                      width={265}
                      height={83}
                      className="pointer-events-none block h-auto w-full select-none object-contain"
                      draggable={false}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isPredictConfirmOpen ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-3">
          <div
            className="absolute inset-0 bg-black/20"
            aria-hidden="true"
            onClick={isLoadingSubmit ? undefined : onCancelMatchConfirm}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-label="Xác nhận dự đoán trận đấu"
            className="relative w-full max-w-[520px] bg-[#d4edf7] border-[2.289px] border-solid border-white rounded-[10px] p-[18px] md:p-[30px] backdrop-blur-[24.266px]"
          >
            <div className="flex justify-center mb-3">
              <div className="w-[68px] h-[68px] md:w-[80px] md:h-[80px] bg-[#4cd964] rounded-full flex items-center justify-center shadow-lg">
                <svg
                  className="w-[40px] h-[40px] md:w-[50px] md:h-[50px] text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>

            <div className="text-center font-extrabold text-[#26a9e0] text-[20px] md:text-[24px] leading-[32px] md:leading-[40px]">
              XÁC NHẬN DỰ ĐOÁN
            </div>

            <div className="mt-2 text-center text-[#2a2a2a] font-medium text-[14px] md:text-[16px] leading-[22px] md:leading-[26px]">
              <div className="mb-2">Bạn đã chọn dự đoán các trận đấu sau:</div>

              <div className="max-h-[300px] overflow-y-auto space-y-2 px-2">
                {pendingMatchPredictions.map((match, index) => (
                  <div key={match.matchId || index} className="bg-white/60 rounded-lg p-2 md:p-3">
                    <div className="font-bold text-[#333]">
                      {match.homeName} - {match.awayName}
                    </div>
                    <div className="text-[#26a8df] font-bold text-[16px] md:text-[18px]">
                      Tỷ số: {match.homeName}{' '}
                      <span className="text-[#e74c3c]">{match.homeScore}</span> -{' '}
                      <span className="text-[#e74c3c]">{match.awayScore}</span> {match.awayName}
                    </div>
                    <div className="text-[#666] text-[12px] md:text-[14px]">
                      Số người dự đoán giống bạn:{' '}
                      <span className="font-bold text-[#26a8df]">{match.sameScore}</span>
                    </div>
                    {match.firstGoal && (
                      <div className="text-[#666] text-[12px] md:text-[14px]">
                        Đội ghi bàn đầu tiên:{' '}
                        <span className="font-bold text-[#26a8df]">
                          {match.firstGoal === 'home' ? match.homeName : match.awayName}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-3 italic text-[13px] md:text-[14px]">
                Hãy cùng chờ đợi kết quả nhé.
              </div>
            </div>

            <div className="mt-4 flex items-center justify-center gap-[18px]">
              <button
                type="button"
                disabled={isLoadingSubmit}
                onClick={onConfirmMatchPrediction}
                className={twMerge(
                  'w-[143.5px] h-[38px] md:h-auto border-none bg-[#26a8df] text-white rounded-[9.965px] px-[9.965px] py-[9.965px] font-semibold text-[13.951px]',
                  isLoadingSubmit ? 'opacity-60 cursor-not-allowed' : ''
                )}
              >
                {isLoadingSubmit ? 'ĐANG GỬI...' : 'XÁC NHẬN'}
              </button>

              <button
                type="button"
                disabled={isLoadingSubmit}
                onClick={onCancelMatchConfirm}
                className={twMerge(
                  'w-[143.5px] h-[38px] md:h-auto border-none bg-[#a2b1b8] text-white rounded-[9.965px] px-[9.965px] py-[9.965px] font-semibold text-[13.951px]',
                  isLoadingSubmit ? 'opacity-60 cursor-not-allowed' : ''
                )}
              >
                HỦY BỎ
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <RulePage isModalRule={isModalRule} setIsModalRule={setIsModalRule} />

      <HistoryPredictions
        isModalVote={isModalHistory}
        setIsModalVote={setIsModalHistory}
        IP={IP}
        FP={FP}
      />
    </div>
  );
};

export default C168Page;
